/**
 * 争议 / 拒付（只读）。
 *
 * 没有「改状态」的入口 —— 胜诉/败诉是卡组织裁决的结果，只能由通道事件驱动，
 * 人手改它等于伪造事实。这里唯一的运营能力是按举证截止日筛选，把快到期的盯住。
 */

import { useMemo, useState } from 'react';
import { usePagedList } from '../lib/hooks.ts';
import { Badge, Card, CopyId, Field, Money, Select, StatusBadge } from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatDateTime, statusLabel } from '../lib/format.ts';
import type { Dispute } from '../lib/types.ts';

const DISPUTE_STATUSES = ['open', 'under_review', 'won', 'lost', 'accepted', 'expired'];
const DISPUTE_STAGES = ['inquiry', 'chargeback', 'pre_arbitration', 'arbitration'];

const STAGE_LABEL: Readonly<Record<string, string>> = {
  inquiry: '调单',
  chargeback: '拒付',
  pre_arbitration: '预仲裁',
  arbitration: '仲裁',
};

/** 距举证截止还剩多久。≤3 天标红 —— 过期等于自动判负。 */
function dueTone(dueBy: string | null): 'bad' | 'warn' | 'neutral' {
  if (dueBy === null) return 'neutral';
  const remainingMs = new Date(dueBy).getTime() - Date.now();
  if (remainingMs < 0) return 'bad';
  if (remainingMs < 3 * 86_400_000) return 'bad';
  if (remainingMs < 7 * 86_400_000) return 'warn';
  return 'neutral';
}

export function DisputesPage() {
  const [status, setStatus] = useState('');
  const [stage, setStage] = useState('');
  const [dueSoon, setDueSoon] = useState(false);

  /*
   * 同 overview 的那条：usePagedList 拿 JSON.stringify(query) 当依赖，
   * 每次渲染现算一个新时间戳就等于每次渲染都换了筛选条件 —— 取数、渲染、再取数。
   * 只在「即将到期」开着的时候才会踩到，所以它比总览那条更难发现。
   */
  const dueBefore = useMemo(
    () => (dueSoon ? new Date(Date.now() + 7 * 86_400_000).toISOString() : null),
    [dueSoon],
  );

  const list = usePagedList<Dispute>('/v1/disputes', {
    status: status === '' ? null : status,
    stage: stage === '' ? null : stage,
    due_before: dueBefore,
  });

  const columns: readonly Column<Dispute>[] = [
    { key: 'id', header: '争议号', render: (row) => <CopyId id={row.id} /> },
    { key: 'payment', header: '原交易', render: (row) => (
      <div className="stack tight">
        <CopyId id={row.payment_id} />
        {row.order_no !== undefined && <span className="cell-muted mono" style={{ fontSize: 11 }}>{row.order_no}</span>}
      </div>
    ) },
    { key: 'stage', header: '阶段', render: (row) => (
      <Badge tone={row.stage === 'arbitration' ? 'bad' : row.stage === 'inquiry' ? 'info' : 'warn'}>
        {STAGE_LABEL[row.stage] ?? row.stage}
      </Badge>
    ) },
    { key: 'amount', header: '金额', numeric: true, render: (row) => (
      <Money value={row.amount} currency={row.currency} />
    ) },
    { key: 'reason', header: '原因码', render: (row) => (
      <span className="mono">{row.reason_code ?? '—'}</span>
    ) },
    { key: 'status', header: '状态', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'due', header: '举证截止', render: (row) => {
      const tone = dueTone(row.due_by);
      return row.due_by === null ? <span className="cell-muted">—</span> : (
        <span className={tone === 'bad' ? 'dr mono' : tone === 'warn' ? 'mono' : 'cell-muted mono'}>
          {formatDateTime(row.due_by)}
        </span>
      );
    } },
    { key: 'external', header: '通道单号', render: (row) => (
      row.external_id === null ? <span className="cell-muted">—</span> : <CopyId id={row.external_id} full />
    ) },
  ];

  return (
    <>
      <h1>争议</h1>

      <div className="filters">
        <Field label="状态">
          <Select
            value={status}
            onChange={setStatus}
            placeholder="全部"
            options={DISPUTE_STATUSES.map((value) => ({ value, label: statusLabel(value) }))}
          />
        </Field>
        <Field label="阶段">
          <Select
            value={stage}
            onChange={setStage}
            placeholder="全部"
            options={DISPUTE_STAGES.map((value) => ({ value, label: STAGE_LABEL[value] ?? value }))}
          />
        </Field>
        <Field label=" ">
          <label className="checkbox">
            <input type="checkbox" checked={dueSoon} onChange={(e) => { setDueSoon(e.target.checked); }} />
            只看 7 天内到期
          </label>
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty="没有符合条件的争议"
          emptyHint="争议数据由通道 webhook 与对账任务写入，控制台不提供手工创建。"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>
    </>
  );
}
